import React, { useRef } from "react";
import { View, ViewStyle, DimensionValue } from "react-native";
import Svg, {
  Defs,
  LinearGradient as SvgLinearGradient,
  Stop,
  Rect,
} from "react-native-svg";
import { LinearGradientProps } from "@xsolla/xui-primitives-core";

let gradientIdCounter = 0;

function parseColor(color: string): { stopColor: string; stopOpacity: number } {
  if (color === "transparent") {
    return { stopColor: "#000000", stopOpacity: 0 };
  }

  const rgbaMatch = color.match(
    /^rgba?\(\s*([\d.]+%?)\s*,\s*([\d.]+%?)\s*,\s*([\d.]+%?)\s*,\s*([\d.]+)\s*\)$/i
  );
  if (rgbaMatch) {
    return {
      stopColor: `rgb(${rgbaMatch[1]}, ${rgbaMatch[2]}, ${rgbaMatch[3]})`,
      stopOpacity: parseFloat(rgbaMatch[4]),
    };
  }

  return { stopColor: color, stopOpacity: 1 };
}

export const LinearGradient: React.FC<LinearGradientProps> = ({
  colors,
  start = { x: 0, y: 0 },
  end = { x: 0, y: 1 },
  locations,
  children,
  position,
  top,
  bottom,
  left,
  right,
  width,
  height,
  borderRadius,
  overflow,
  zIndex,
  style,
  testID,
  "data-testid": dataTestId,
}) => {
  const containerStyle: ViewStyle = {
    position: position as ViewStyle["position"],
    top: top as DimensionValue,
    bottom: bottom as DimensionValue,
    left: left as DimensionValue,
    right: right as DimensionValue,
    width: width as DimensionValue,
    height: height as DimensionValue,
    borderRadius: typeof borderRadius === "number" ? borderRadius : undefined,
    overflow,
    zIndex,
    ...(style as ViewStyle),
  };

  const gradientId = useRef(`xui-lg-${++gradientIdCounter}`).current;

  const stops = colors.map((color, index) => {
    const offset = locations ? locations[index] : index / (colors.length - 1);
    const { stopColor, stopOpacity } = parseColor(color);
    return (
      <Stop
        key={index}
        offset={offset}
        stopColor={stopColor}
        stopOpacity={stopOpacity}
      />
    );
  });

  return (
    <View style={containerStyle} testID={dataTestId || testID}>
      <Svg
        style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0 }}
        width="100%"
        height="100%"
      >
        <Defs>
          <SvgLinearGradient
            id={gradientId}
            x1={`${start.x * 100}%`}
            y1={`${start.y * 100}%`}
            x2={`${end.x * 100}%`}
            y2={`${end.y * 100}%`}
          >
            {stops}
          </SvgLinearGradient>
        </Defs>
        <Rect
          x="0"
          y="0"
          width="100%"
          height="100%"
          fill={`url(#${gradientId})`}
        />
      </Svg>
      {children}
    </View>
  );
};
